<script setup lang="ts">
/**
 * LanguageSwitcher
 * --------------------------------------------------------------------
 * 两种展示形态：
 *   - inline：平铺链接（AppFooterV3 / index3）
 *   - button：圆角按钮 + 下拉菜单（AppFooter 暗色页脚）
 *
 * 切换行为（沿用现有自研 i18n 体系，见 app/i18n/index.ts + plugins/i18n.ts）：
 *   - 调用 i18n.setLocale(code) → 写入 localStorage + cookie → 反应式更新 → 全站 t() 立即生效。
 *   - 刷新时 SSR 从 cookie 读取同一 locale，避免先英文后日文的闪烁。
 *   - 所有正式页面切换到对应的 locale 路径。
 *   - Sanity 详情页仅允许跳转到明确存在的同级语言页面，避免产生不存在的译文 URL。
 */
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from '#app';
import { Languages } from 'lucide-vue-next';
import { i18n, languages, t } from '~/i18n';
import {
  SEO_PAGE_FAMILIES,
  localizedPath,
  normalizeSiteLocale,
  stripLocalePrefix,
} from '@/lib/site-locale';
import { useSeoDetailLanguagePaths } from '@/composables/useSeoDetailLanguageNavigation';

interface Props {
  /** inline：白底 Footer 平铺链接；button：圆角按钮 + 下拉 */
  mode?: 'inline' | 'button';
  /** light：白底；dark：黑底（仅 button 模式使用） */
  variant?: 'light' | 'dark';
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'inline',
  variant: 'light',
});

const current = computed(() => i18n.locale);
const isMenuOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const route = useRoute();
const router = useRouter();
const seoDetailLanguagePaths = useSeoDetailLanguagePaths();

const navClass = computed(() =>
  props.variant === 'dark'
    ? 'flex items-center gap-3 text-[12px] font-normal text-white/50'
    : 'flex items-center gap-3 text-[12px] font-normal text-ink-faint'
);

const activeClass = computed(() =>
  props.variant === 'dark' ? 'text-white/90 cursor-default' : 'text-ink-strong cursor-default'
);

const inactiveClass = computed(() =>
  props.variant === 'dark'
    ? 'hover:text-white/90 cursor-pointer'
    : 'hover:text-ink-strong cursor-pointer'
);

const separatorClass = computed(() =>
  props.variant === 'dark' ? 'text-white/30' : 'text-ink-faint/50'
);

const buttonClass = computed(() =>
  props.variant === 'dark'
    ? 'flex items-center gap-1 md:gap-2 border border-white/5 text-white/60 hover:text-white/90 font-medium px-2 md:px-3 py-1.5 md:py-2 rounded-full hover:bg-white/5 transition-all duration-200 text-xs md:text-sm'
    : 'flex items-center gap-1 md:gap-2 border border-black/10 text-black/60 hover:text-black/90 font-medium px-2 md:px-3 py-1.5 md:py-2 rounded-full hover:bg-black/[0.03] transition-all duration-200 text-xs md:text-sm'
);

const currentLabel = computed(
  () => languages.find((lang) => lang.code === current.value)?.label ?? current.value
);

const currentCode = computed(
  () =>
    languages.find((lang) => lang.code === current.value)?.code.toUpperCase() ??
    current.value.toUpperCase()
);

type SeoRouteKind = 'index' | 'pagination' | 'detail' | 'other';

const seoRouteKind = (path: string): SeoRouteKind => {
  const unprefixedPath = stripLocalePrefix(path);
  const segments = unprefixedPath.split('/').filter(Boolean);
  const [section] = segments;
  const isSeoSection =
    section === 'blog' ||
    section === 'spotlight' ||
    SEO_PAGE_FAMILIES.includes(section as (typeof SEO_PAGE_FAMILIES)[number]);

  if (!isSeoSection) return 'other';
  if (segments.length === 1) return 'index';
  if (
    (section === 'blog' || section === 'spotlight') &&
    segments.length === 3 &&
    segments[1] === 'page' &&
    /^\d+$/.test(segments[2] ?? '')
  ) {
    return 'pagination';
  }
  if (
    (section === 'ai-design-tools' || section === 'spotlight') &&
    segments.length === 3 &&
    segments[1] === 'categories'
  ) {
    return 'index';
  }
  return 'detail';
};

const canSelectLanguage = (code: string): boolean => {
  const nextLocale = normalizeSiteLocale(code) ?? 'en';
  if (nextLocale === current.value) return true;
  if (seoDetailLanguagePaths) {
    return Boolean(seoDetailLanguagePaths.value[nextLocale]);
  }
  return seoRouteKind(route.path) !== 'detail';
};

const unavailableClass = computed(() =>
  props.variant === 'dark'
    ? 'cursor-not-allowed text-white/25'
    : 'cursor-not-allowed text-ink-faint/40'
);

const languageButtonClass = (code: string) => {
  if (code === current.value) return activeClass.value;
  return canSelectLanguage(code) ? inactiveClass.value : unavailableClass.value;
};

const languageMenuItemClass = (code: string) => {
  const isCurrent = current.value === code;
  const isAvailable = canSelectLanguage(code);
  return {
    'cursor-default font-medium text-black/90': isCurrent,
    'cursor-pointer text-black/50': !isCurrent && isAvailable,
    'cursor-not-allowed text-black/25 hover:bg-transparent': !isAvailable,
  };
};

const languageRoutePath = (nextLocale: ReturnType<typeof normalizeSiteLocale>): string | null => {
  if (!nextLocale) return null;
  if (seoDetailLanguagePaths) {
    return seoDetailLanguagePaths.value[nextLocale] ?? null;
  }
  return localizedPath(nextLocale, stripLocalePrefix(route.path));
};

const handleSelect = (code: string) => {
  if (code === current.value) {
    isMenuOpen.value = false;
    return;
  }
  const nextLocale = normalizeSiteLocale(code) ?? 'en';
  const nextPath = languageRoutePath(nextLocale);
  if (!canSelectLanguage(nextLocale)) {
    // Detail pages may only switch to an explicitly provided sibling. This
    // also protects detail families that do not yet provide sibling metadata.
    isMenuOpen.value = false;
    return;
  }

  i18n.setLocale(nextLocale);
  isMenuOpen.value = false;
  if (!nextPath || nextPath === route.path) return;
  const query = { ...route.query };
  delete query.lang;
  void router.push({ path: nextPath, query, hash: route.hash });
};

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const handleDocumentClick = (event: MouseEvent) => {
  if (!rootRef.value?.contains(event.target as Node)) {
    isMenuOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick);
});
</script>

<template>
  <nav v-if="mode === 'inline'" :class="navClass" :aria-label="t('languageSwitcher.aria_label')">
    <template v-for="(lang, idx) in languages" :key="lang.code">
      <button
        type="button"
        class="transition-colors"
        :class="languageButtonClass(lang.code)"
        :disabled="!canSelectLanguage(lang.code)"
        :aria-disabled="!canSelectLanguage(lang.code) ? 'true' : undefined"
        :aria-current="lang.code === current ? 'true' : undefined"
        @click="handleSelect(lang.code)"
      >
        {{ lang.label }}
      </button>
      <span v-if="idx < languages.length - 1" :class="separatorClass" aria-hidden="true">·</span>
    </template>
  </nav>

  <div v-else ref="rootRef" class="relative" :aria-label="t('languageSwitcher.aria_label')">
    <button type="button" :class="buttonClass" @click.stop="toggleMenu">
      <Languages class="size-3 md:size-4" />
      <span class="hidden sm:inline">{{ currentLabel }}</span>
      <span class="sm:hidden">{{ currentCode }}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-3 h-3 md:w-4 md:h-4 transition-transform"
        :class="{ 'rotate-180': isMenuOpen }"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>

    <div
      v-if="isMenuOpen"
      class="absolute bottom-full right-0 z-50 mb-2 w-[192px] rounded-[20px] border border-black/10 bg-white/90 p-2 shadow-[0_3px_10px_0_rgba(0,0,0,0.07)] backdrop-blur-md"
    >
      <button
        v-for="lang in languages"
        :key="lang.code"
        type="button"
        class="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors duration-200 hover:bg-black/5"
        :class="languageMenuItemClass(lang.code)"
        :disabled="!canSelectLanguage(lang.code)"
        :aria-disabled="!canSelectLanguage(lang.code) ? 'true' : undefined"
        @click="handleSelect(lang.code)"
      >
        <span class="text-sm">{{ lang.label }}</span>
        <svg
          v-if="current === lang.code"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-black/90"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </button>
    </div>
  </div>
</template>
